/**
 * Authentication and authorisation — the decision logic, with no I/O.
 *
 * Everything here is a pure function of its arguments: `now` is passed in, hashing is passed
 * in. That is what lets "is this session still valid" and "does this action need step-up" be
 * tested exhaustively, including the boundaries, without freezing the clock or standing up a
 * database. The argon2/TOTP/HTTP parts live in apps/api, where the I/O belongs.
 *
 * Rules implemented here come from docs/SECURITY.md §2. If the two ever disagree, the doc is
 * the specification and this file is the bug.
 */

// ── Roles ────────────────────────────────────────────────────────────────────

export const ROLES = [
  // No `owner_portal`. It sat here from the start, was never assignable and granted nothing, and
  // a portal login is not a membership at all — see `portal_access` (0128) and
  // packages/core/src/portal.ts for why somebody outside the agency must never hold a role that
  // runs through the staff policies.
  'property_manager',
  // Job titles an agency assigns responsibility for a property with. They carry no capability
  // of their own — see PROPERTY_MANAGER_ROLES in ./access.ts for why that is deliberate.
  'letting_agent',
  'business_development_manager',
  'inspection_manager',
  'accounts',
  'trust_admin',
  'org_admin',
  // Ours, not a customer's. Neither can be granted by an agency to itself — see
  // PLATFORM_ONLY_ROLES in ./access.ts.
  'support',
  'platform_admin',
] as const;

export type Role = (typeof ROLES)[number];

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

/**
 * Roles that may post journals or move money.
 *
 * `support` is deliberately absent. Our own staff can help a customer read their books and
 * can never touch them — docs/DATA_MODEL.md "Roles", and it is the one line in that table
 * that protects us as much as the customer.
 */
export const TRUST_ROLES: readonly Role[] = ['accounts', 'trust_admin'];

/** Roles for which TOTP is mandatory. Not a setting an org can turn off. */
export const MFA_REQUIRED_ROLES: readonly Role[] = [
  'accounts',
  'trust_admin',
  'org_admin',
  'support',
  'platform_admin',
];

export function hasRole(roles: readonly string[], wanted: Role): boolean {
  return roles.includes(wanted);
}

export function hasAnyRole(roles: readonly string[], wanted: readonly Role[]): boolean {
  return wanted.some((role) => roles.includes(role));
}

/** True when this set of roles may post a journal at all, before step-up is considered. */
export function canPostJournals(roles: readonly string[]): boolean {
  return hasAnyRole(roles, TRUST_ROLES);
}

/** True when this user must have TOTP enrolled before they can use the system. */
export function mfaRequired(roles: readonly string[]): boolean {
  return hasAnyRole(roles, MFA_REQUIRED_ROLES);
}

/**
 * Whether a session for these roles must produce a code before it can do anything.
 *
 * The rule for EVERY way a session comes into being — a login and an organisation switch alike.
 * It was written out separately in each once, and the switch's copy asked only whether the user
 * had an authenticator: a login with none, switching into an agency where its role demands one,
 * arrived MFA-satisfied without ever enrolling. One function means one rule.
 *
 * Enrolled counts on its own: somebody who volunteered a second factor does not have it quietly
 * skipped because their roles here do not insist on it.
 */
export function demandsSecondFactor(roles: readonly string[], enrolled: boolean): boolean {
  return enrolled || mfaRequired(roles);
}

// ── Which agency a login opens in ────────────────────────────────────────────

/** What `defaultMembership` needs to know about one of a login's live memberships. */
export interface MembershipChoice {
  orgId: string;
  /** Time-boxed support access into a customer, rather than the person's own agency. */
  isSupport: boolean;
  /** When the person was added to this agency. */
  createdAt: Date;
  /** The person's own stored choice of where to open. */
  isDefault: boolean;
}

/**
 * The agency a sign-in opens in, from the memberships that are live right now.
 *
 * Sign-in does not ask and does not list: the list of agencies a login belongs to used to be the
 * answer to a right password, before any second factor. So the choice is made here instead —
 *
 *   1. the one the person chose as their default, if their access to it is still live;
 *   2. otherwise the agency they were set up in FIRST.
 *
 * SUPPORT ACCESS IS NEVER WHERE A LOGIN OPENS unless it is all there is. It expires by itself, and
 * a support engineer whose morning sign-in landed inside a customer's books — because that grant
 * happened to be their oldest row — would be in somebody's trust account without having meant to
 * go there. `canBeDefault` is the same line, asked when a default is being SET.
 *
 * Callers pass only memberships that are active and unexpired, so a stored default pointing at an
 * agency the person has left is simply absent, and rule 2 answers. Ties on the timestamp — two
 * memberships written in one transaction — break on the org id, so the answer never depends on
 * the order rows happened to arrive in.
 */
export function defaultMembership<T extends MembershipChoice>(live: readonly T[]): T | undefined {
  const own = live.filter(canBeDefault);
  const chosen = own.find((membership) => membership.isDefault);
  if (chosen) return chosen;

  const oldestFirst = (a: T, b: T) =>
    a.createdAt.getTime() - b.createdAt.getTime() || (a.orgId < b.orgId ? -1 : 1);
  return [...(own.length > 0 ? own : live)].sort(oldestFirst)[0];
}

/** Whether a membership may be somebody's default: their own agency, not borrowed access. */
export function canBeDefault(membership: Pick<MembershipChoice, 'isSupport'>): boolean {
  return !membership.isSupport;
}

// ── Session lifetime ─────────────────────────────────────────────────────────

/** Idle timeout: a session unused for this long is dead. */
export const SESSION_IDLE_MS = 4 * 60 * 60 * 1000;

/** Absolute lifetime: even a continuously used session must be re-established weekly. */
export const SESSION_ABSOLUTE_MS = 7 * 24 * 60 * 60 * 1000;

/** How recently MFA must have been satisfied for a money-moving action. */
export const STEP_UP_WINDOW_MS = 15 * 60 * 1000;

/**
 * How long an invited user has to set their first password.
 *
 * HERE RATHER THAN IN THE ROUTER THAT ISSUES ONE, because two routers now issue them: an
 * agency administrator inviting a colleague, and a platform administrator opening an agency
 * and its first administrator. Two constants would drift, and the drift would be invisible —
 * a link that expires on a different day depending on who sent it.
 */
export const SETUP_TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface SessionTimes {
  createdAt: Date;
  lastSeenAt: Date;
  revokedAt: Date | null;
}

export type SessionState = 'active' | 'revoked' | 'idle_expired' | 'absolute_expired';

/**
 * Why a session is or is not usable.
 *
 * Returns the reason rather than a boolean: "expired because you were away" and "expired
 * because a week has passed" are the same to the caller but not to the audit log, and
 * `revoked` after a password change is a security event worth seeing.
 *
 * Checked in order of severity — a revoked session is revoked whatever the clock says.
 */
export function sessionState(session: SessionTimes, now: Date): SessionState {
  if (session.revokedAt !== null) return 'revoked';

  const age = now.getTime() - session.createdAt.getTime();
  if (age >= SESSION_ABSOLUTE_MS) return 'absolute_expired';

  const idle = now.getTime() - session.lastSeenAt.getTime();
  if (idle >= SESSION_IDLE_MS) return 'idle_expired';

  return 'active';
}

export function isSessionUsable(session: SessionTimes, now: Date): boolean {
  return sessionState(session, now) === 'active';
}

/**
 * How stale `last_seen_at` may get before a request slides it forward.
 *
 * A COST DECISION (CLAUDE.md §1.5). Touching on every request made every authenticated call —
 * including the notifications poll from every open tab — an `UPDATE` on `sessions`: a WAL record,
 * a dead tuple for vacuum, and a second round trip. Against a four-hour idle window, recording
 * "seen" to the nearest five minutes changes when a session expires by at most five minutes, in
 * the direction of expiring SOONER, which is the safe direction for it to be wrong.
 */
export const SESSION_TOUCH_INTERVAL_MS = 5 * 60 * 1000;

/** True when this request should record the session as seen. */
export function sessionNeedsTouch(lastSeenAt: Date, now: Date): boolean {
  return now.getTime() - lastSeenAt.getTime() >= SESSION_TOUCH_INTERVAL_MS;
}

/**
 * Whether a money-moving action needs MFA re-verified first.
 *
 * A long-lived session that can quietly move trust money is exactly the account-takeover
 * payoff we are designing against, so this errs closed: never verified means step-up.
 */
export function needsStepUp(mfaVerifiedAt: Date | null, now: Date): boolean {
  if (mfaVerifiedAt === null) return true;
  return now.getTime() - mfaVerifiedAt.getTime() > STEP_UP_WINDOW_MS;
}

// ── Login throttling ─────────────────────────────────────────────────────────

/** Failures before the account is locked. Per account; per-IP limiting is separate. */
export const MAX_FAILED_ATTEMPTS = 8;

/**
 * The ceiling, named first so it is the last step BY CONSTRUCTION rather than by counting.
 *
 * `lockoutMs` needs a fallback for an index it has already clamped into range, and the
 * fallback has to be the longest lockout — failing towards "keep the attacker out" rather
 * than "let them carry on guessing". Deriving it from the table would reintroduce exactly the
 * unchecked index it exists to avoid.
 */
const MAX_LOCKOUT_MS = 60 * 60 * 1000;

const LOCKOUT_STEPS_MS = [
  60 * 1000, // 8 failures  → 1 min
  5 * 60 * 1000, // 9        → 5 min
  15 * 60 * 1000, // 10      → 15 min
  MAX_LOCKOUT_MS, // 11+     → 1 hour, and stays there
] as const;

/**
 * How long to lock an account after `failedAttempts` consecutive failures.
 *
 * Progressive rather than a single cliff: an attacker is slowed exponentially while a real
 * person who has fat-fingered their password twice more is not locked out of their job for
 * an hour. Returns 0 while under the threshold.
 *
 * Deliberately caps rather than growing forever — an unbounded lockout is a denial of service
 * an attacker can inflict on a known email address.
 */
export function lockoutMs(failedAttempts: number): number {
  if (failedAttempts < MAX_FAILED_ATTEMPTS) return 0;
  const step = Math.min(failedAttempts - MAX_FAILED_ATTEMPTS, LOCKOUT_STEPS_MS.length - 1);
  // `step` is clamped into range, so the fallback is unreachable — but it falls back to the
  // LONGEST lockout rather than to zero. If this is ever wrong, it must fail in the direction
  // that keeps an attacker out, not the one that lets them keep guessing.
  return LOCKOUT_STEPS_MS[step] ?? MAX_LOCKOUT_MS;
}

/**
 * `lockoutMs` as a table, for the database: element `n - 1` is the lock after `n` failures, and
 * any count past the end takes the last element.
 *
 * The login path charges an attempt and applies its lock in ONE statement
 * (`auth_charge_login_attempt`), because deciding the lock in the application from a count read
 * before the ~60ms password check let concurrent guesses all see the same stale count, none of
 * them lock, and a burst sail past the threshold. That statement needs the ladder, and passing it
 * in keeps the policy here rather than restated in SQL where it could drift.
 */
export function lockoutSchedule(): number[] {
  return Array.from({ length: MAX_FAILED_ATTEMPTS + LOCKOUT_STEPS_MS.length }, (_, index) =>
    lockoutMs(index + 1),
  );
}

export function isLockedOut(lockedUntil: Date | null, now: Date): boolean {
  return lockedUntil !== null && lockedUntil.getTime() > now.getTime();
}

// ── Credentials ──────────────────────────────────────────────────────────────

/**
 * Email, normalised for comparison and storage.
 *
 * Lowercased and trimmed only. Deliberately NOT "gmail dot-stripping" or plus-address
 * removal: those are provider-specific guesses, and getting one wrong silently merges two
 * different people — which in this system means merging two different trust positions.
 */
export function normaliseEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

export const MIN_PASSWORD_LENGTH = 12;

export interface PasswordContext {
  email?: string | undefined;
  name?: string | undefined;
}

/**
 * Password problems, as messages for the user. Empty means acceptable.
 *
 * Length and reuse-of-personal-data only — no composition rules. Forcing a symbol and a digit
 * produces `Password1!` and measurably worse passwords; NIST dropped that guidance years ago.
 * The real protection is the length floor plus the breach check, which happens in the API
 * because it needs the network.
 */
export function passwordIssues(password: string, context: PasswordContext = {}): string[] {
  const issues: string[] = [];

  if (password.length < MIN_PASSWORD_LENGTH) {
    issues.push(`Use at least ${MIN_PASSWORD_LENGTH} characters`);
  }
  // Cap it: argon2 hashes the whole input, and a megabyte password is a free CPU-burn
  // request. 200 is far past any real passphrase.
  if (password.length > 200) {
    issues.push('Use at most 200 characters');
  }

  const lower = password.toLowerCase();
  const email = context.email?.trim().toLowerCase();
  if (email && (lower.includes(email) || email.includes(lower))) {
    issues.push('Do not use your email address as your password');
  }

  const name = context.name?.trim().toLowerCase();
  if (name && name.length >= 4 && lower.includes(name)) {
    issues.push('Do not use your name as your password');
  }

  return issues;
}

// ── Audit hash chain ─────────────────────────────────────────────────────────

/**
 * Deterministic JSON: object keys sorted at every depth.
 *
 * The audit chain hashes this string, so two runs that mean the same thing must produce the
 * same bytes. `JSON.stringify` preserves insertion order, which means the same event built by
 * two code paths could hash differently and look like tampering.
 *
 * `undefined` is dropped (as JSON does); `null` is kept, because "explicitly cleared" and
 * "not mentioned" are different facts in an audit record.
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';

  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
  }

  // bigint has no JSON representation, and money is bigint cents everywhere in this codebase.
  // Serialise it as a string rather than throwing deep inside an audit write.
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, v]) => `${JSON.stringify(key)}:${canonicalJson(v)}`);

  return `{${entries.join(',')}}`;
}

/** Serialise bigint before hashing — audit payloads carry money. */
function replaceBigints(value: unknown): unknown {
  if (typeof value === 'bigint') return `${value.toString()}n`;
  if (Array.isArray(value)) return value.map(replaceBigints);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, replaceBigints(v)]),
    );
  }
  return value;
}

/**
 * The exact string an audit row hashes.
 *
 * Separate from the hashing itself so the input is testable without a hash function, and so
 * the chain rule is stated in one place: each row commits to the row before it, therefore
 * altering any historical row invalidates every row after it.
 */
export function auditHashInput(previousHash: string | null, payload: unknown): string {
  return `${previousHash ?? ''}|${canonicalJson(replaceBigints(payload))}`;
}

/**
 * The hash for the next link in the chain.
 *
 * `sha256` is injected — packages/core does no I/O and reaches for no ambient capability,
 * and it makes the chain logic testable with a fake digest.
 */
export function chainHash(
  previousHash: string | null,
  payload: unknown,
  sha256: (input: string) => string,
): string {
  return sha256(auditHashInput(previousHash, payload));
}

/**
 * Verify a chain end to end. Returns the index of the first broken link, or -1 if intact.
 *
 * The nightly verifier calls this. A break means either a bug or tampering, and both need to
 * be loud — an audit log nobody checks is decoration.
 */
export function findChainBreak(
  entries: readonly { hash: string; payload: unknown }[],
  sha256: (input: string) => string,
  startingFrom: string | null = null,
): number {
  let previous = startingFrom;
  for (const [i, entry] of entries.entries()) {
    if (chainHash(previous, entry.payload, sha256) !== entry.hash) return i;
    previous = entry.hash;
  }
  return -1;
}
